/**
 * PdfPickerModal — 도면 PDF를 "서버 목록에서 선택" 또는 "새로 업로드" 중
 * 하나로 얻어 File 객체를 돌려주는 오케스트레이터. 기본 화면은 목록(선택)
 * 이고, 업로드는 목록 화면의 버튼으로 들어간다(DXF 업로드 단계와 동일 패턴).
 *
 * 실제 CCTV/구역 매핑 로직(handleCctvZoneMapping)은 그대로 File 을 받는
 * 형태라 이 모달은 그 앞단에서 "어떻게 파일을 구할지"만 담당한다 — 목록에서
 * 고른 파일은 다운로드해 File 로 재구성하고, 새로 업로드한 파일은 서버에
 * 영구 저장(다른 유저도 목록에서 볼 수 있게)한 뒤 로컬 File 을 그대로 쓴다.
 */
import { useState } from 'react'
import { FileStorageService } from '../../../services/FileStorageService'
import { FileInfo } from '../../../models/FileStorage'
import { ServerPDFList } from './ServerPDFList'
import { LocalPDFUploader } from './LocalPDFUploader'
import styles from './PdfPickerModal.module.css'

interface PdfPickerModalProps {
  siteProjectId: number
  onFileReady: (file: File) => void
  onCancel: () => void
}

type Mode = 'list' | 'upload'

export function PdfPickerModal({ siteProjectId, onFileReady, onCancel }: PdfPickerModalProps) {
  const [mode, setMode] = useState<Mode>('list')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSelectExisting = async (file: FileInfo) => {
    setBusy(true)
    setError(null)
    try {
      const blob = await FileStorageService.downloadFile(String(siteProjectId), 'pdf', file.filename)
      const localFile = new File([blob], file.original_name || file.filename, { type: 'application/pdf' })
      onFileReady(localFile)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF 파일을 불러오지 못했습니다.')
      setBusy(false)
    }
  }

  const handleUpload = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const result = await FileStorageService.uploadFiles(String(siteProjectId), 'pdf', [file])
      if (!result.success) {
        throw new Error(result.message || 'PDF 업로드에 실패했습니다.')
      }
      onFileReady(file)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF 업로드에 실패했습니다.')
      setBusy(false)
    }
  }

  if (mode === 'upload') {
    return (
      <div className={styles.stackingContext}>
        <LocalPDFUploader
          onFileSelect={(file) => void handleUpload(file)}
          onCancel={() => setMode('list')}
          uploading={busy}
        />
        {error && <PdfPickerError message={error} />}
      </div>
    )
  }

  return (
    <div className={styles.stackingContext}>
      <ServerPDFList
        projectId={siteProjectId}
        onFileSelect={(file) => void handleSelectExisting(file)}
        onCancel={onCancel}
        onUploadClick={() => setMode('upload')}
      />
      {error && <PdfPickerError message={error} />}
    </div>
  )
}

/** 목록/업로드 오버레이 위에 겹쳐 보여주는 간단한 에러 토스트. */
function PdfPickerError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        background: '#dc2626',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 8,
        fontSize: 13,
        zIndex: 10001,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      }}
    >
      {message}
    </div>
  )
}
